import { cloneDeep } from 'lodash'
import { getAllMSServicesFromSiloOverview } from '../siloRelated/getAllMSServicesFromSiloOverview'
import { CompanyContact } from './CompanyContact'
import { SupplierAndTsdDescr } from './SupplierAndTsdDescr'
import { countUniqueStatii } from './StatiiCounts'
import type { IContract } from '~/types/contract/models'
import type { TSiloOverview } from '~/types/silo/models'
import type { IOrder, IOrderOverview } from '~/types/order/models'
import type { ICompanyOverview } from '~/types/company/models'
import {
  ContractTypeIds,
  hasMSAContractType,
  hasSAContractType,
  hasSAOrNonContractContractType,
} from '~/services/selectLists/contractTypes'
import {
  isIOpportunityOverviewData,
  isIOrderOverviewData,
  isICompanyOverviewData,
} from '~/types/silo/models'
import { assertIsNotUndefined } from '~/types/models'
import { ContractStatusIds } from '~/services/selectLists/contractStatii'
import {
  OrderMSServicesStatusIds,
  getOrderMSServiceStatusById,
} from '~/services/selectLists/orderMSServicesStatii'
import {
  CompanyMSServicesStatusIds,
  getCompanyMSServiceStatusById,
} from '~/services/selectLists/companyMSServicesStatii'
import { getSupplierAvatar } from '~/cynqueModules/dataObjects/Supplier'

const mn = `[cynqueModules/dataObjects/Contract.ts]`

export function Contract(o: IContract): IContract {
  const fn = `${mn}] Contract |`
  if (!o) {
    throw new Error(`${fn} Contract called with falsey contract`)
  }

  const obj = cloneDeep(o) // To avoid mutating Vuex property directly
  // const obj = { ...o }

  if (!obj.supplier) {
    throw new Error(`${fn} Contract called with falsey supplier`)
  }

  // o.supplier should be a select item,
  // specifically, an IEntitySelectItemWithOptionalAvatar
  assertIsNotUndefined(o.supplier.label)
  const supplierSelectItem = { id: o.supplier.id, label: o.supplier.label }
  const tsd = o.tsdSupplier?.tsd

  obj.supplierAndTsdDescr = SupplierAndTsdDescr({
    supplier: supplierSelectItem,
    tsd: tsd ? { id: tsd.id, label: tsd.label } : undefined,
  })

  if (o.signedByCompanyContact) {
    obj.signedByCompanyContact = CompanyContact(o.signedByCompanyContact)
  }

  // obj.signedOnByDescr =
  //   (obj.signedByCompanyContact?.label ?? '') + '\n' + obj.signedOnDate

  obj.signedOnAcceptedOnDescr =
    (obj.signedOnDate ?? ' ') + '\n' + (obj.acceptedBySupplierDate ?? ' ')

  // A cleared Start Date is stored as '' (datePickerChanged), which makes an
  // Invalid Date whose toISOString() throws: skip it, as for null.
  const startDate =
    typeof obj.startDate === 'string' ? new Date(obj.startDate) : undefined
  if (
    startDate &&
    !Number.isNaN(startDate.getTime()) &&
    typeof obj.term === 'number' &&
    obj.term > 0
  ) {
    const endDate = new Date(
      startDate.setMonth(startDate.getMonth() + obj.term)
    )
    obj.endDate = endDate.toISOString().substring(0, 10)
  }

  obj.startOnEndOnDescr = obj.startDate + '\n' + (obj.endDate ?? ' ')

  const parentContractNumberLabel = `${
    obj.parentContract?.contractType.label ?? ''
  }${obj.parentContract ? ' | ' : ''}${
    obj.parentContract?.contractNumber ?? ''
  }`

  obj.parentContractNumberPlusContractNumberDescr =
    obj.contractNumber + '\n' + parentContractNumberLabel

  if (!obj.supplier && obj.contractType.id !== ContractTypeIds.MSA) {
    throw new Error(`${fn} Contract called with falsey supplier`)
  }

  obj.label = `${obj.supplier.label} | ${obj.contractType.label} | #${obj.contractNumber} | ${obj.descr}`
  obj.supplier.avatar = getSupplierAvatar(obj.supplier.label)

  return obj
}

export function propagateContractData({
  callingFn,
  overviewData,
}: {
  callingFn?: string
  overviewData: TSiloOverview
}) {
  const fn = `${callingFn} --> ${mn} propagateContractData |`
  console.debug(`${fn}`)

  // Note: in if...else stanzas below, propagation to
  // MSServices has to preceed propagation to silo root
  // (such as order) cause the latter relies on the former.

  if (isIOpportunityOverviewData(overviewData)) {
    // currently a No-op (except for contract --> contracts)
  } else if (isIOrderOverviewData(overviewData)) {
    propagateContractDataToMOMSServices({
      overviewData,
    })
    propagateContractDataToOrder({
      contracts: overviewData.contracts,
      order: overviewData.order,
    })
  } else if (isICompanyOverviewData(overviewData)) {
    propagateContractDataToCMSServices({
      overviewData,
    })
  } else {
    throw new Error(
      `${fn} NOT AN OPPORTUNITY-OVERVIEW, ORDER-OVERVIEW, OR` +
        ` COMPANY-OVERVIEW. SHOULD NEVER HAPPEN.`
    )
  }
  propagateContractDataToContracts({
    contracts: overviewData.contracts,
  })
}

function propagateContractDataToOrder({
  contracts,
  order,
}: {
  contracts: IContract[]
  order: IOrder
}) {
  const fn = `${mn} propagateContractDataToOrder |`
  if (!contracts) {
    console.debug(`${fn} contracts is falsey. Nothing to propagate.`)
    return
  }
  console.debug(`${fn} propagating based on this contract info...`, contracts)

  // SA ONLY, deliberately — Non-Contract is NOT counted here. Reviewed
  // 2026-08-20 and confirmed intended, unlike the canBeDeleted roll-up below
  // where omitting Non-Contract was a bug. Pinned by Contract.test.ts so it does
  // not get "corrected" later.
  //
  // Keys off the contractType ID; this used to compare `contractType.label` to
  // the string 'SA', which is display text rather than identity.
  const filteredContracts = contracts.filter(hasSAContractType)
  order.numContracts = filteredContracts?.length ?? 0
  order.contractStatusCounts = countUniqueStatii(filteredContracts)
}

function propagateContractDataToMOMSServices({
  overviewData,
}: {
  overviewData: IOrderOverview
}) {
  const fn = `${mn} propagateContractDataToMOMSServices |`
  assertIsNotUndefined(overviewData)
  assertIsNotUndefined(overviewData.contracts)
  const allMOMSServices = getAllMSServicesFromSiloOverview({
    overviewData,
  })
  console.debug(`${fn} allMOMSServices: `, allMOMSServices)
  overviewData.contracts.forEach((contract) => {
    if (
      typeof contract.status.id === 'number' &&
      contract.status.id >= ContractStatusIds.SIGNED &&
      contract.status.id < ContractStatusIds.ACCEPTED
    ) {
      allMOMSServices
        .filter((momss) => momss.contract?.id === contract.id)
        .forEach((momss) => {
          if (momss.status.id === OrderMSServicesStatusIds.PREORDER_UNLOCKED) {
            momss.status = getOrderMSServiceStatusById(
              OrderMSServicesStatusIds.PREORDER_LOCKED
            )
          }
        })
    }
    if (contract.status.id === ContractStatusIds.ACCEPTED) {
      allMOMSServices
        .filter((momss) => momss.contract?.id === contract.id)
        .forEach((momss) => {
          if (
            momss.status.id === OrderMSServicesStatusIds.PREORDER_UNLOCKED ||
            momss.status.id === OrderMSServicesStatusIds.PREORDER_LOCKED
          ) {
            momss.status = getOrderMSServiceStatusById(
              OrderMSServicesStatusIds.READY_FOR_SERVICEORDER
            )
          }
        })
    }
  })
}

function propagateContractDataToCMSServices({
  overviewData,
}: {
  overviewData: ICompanyOverview
}) {
  const fn = `${mn} propagateContractDataToCMSServices |`
  assertIsNotUndefined(overviewData?.contracts)
  const allCMSServices = getAllMSServicesFromSiloOverview({
    overviewData,
  })
  console.debug(`${fn} ${allCMSServices.length} allCMSServices`)
  overviewData.contracts.forEach((contract) => {
    if (
      typeof contract.status.id === 'number' &&
      contract.status.id >= ContractStatusIds.SIGNED &&
      contract.status.id < ContractStatusIds.ACCEPTED
    ) {
      allCMSServices
        .filter((cmss) => cmss.contract?.id === contract.id)
        .forEach((cmss) => {
          if (cmss.status.id === CompanyMSServicesStatusIds.ASSIGNED) {
            cmss.status = getCompanyMSServiceStatusById(
              CompanyMSServicesStatusIds.ACTIVE
            )
          }
        })
    }
  })
}

function propagateContractDataToContracts({
  contracts,
}: {
  contracts: IContract[]
}) {
  const fn = `${mn} propagateContractDataToContracts |`
  if (!contracts) {
    console.debug(`${fn} contracts is falsey. Nothing to propagate.`)
    return
  }
  console.debug(`${fn} propagating based on this contract info...`, contracts)

  // Note: This is really only usefull client-side. CanBeDeleted
  // is set via SQL queries server-side for initial return of
  // contract list. When it needs to be changed server-side,
  // the -entire- list of SA contracts for a given company and supplier
  // needs to be taken into account, not just the SA contracts on this
  // order or opportunity. However, it's more trouble than it's worth
  // to have separate propagation methods for client-side and server-side,
  // and this won't hurt anything if it runs, it's just not comprehensive.

  // An MSA is deletable while nothing hangs off it.
  const msaContracts = contracts.filter(hasMSAContractType)
  msaContracts.forEach((msaContract) => {
    msaContract.canBeDeleted = !contracts.some(
      (contract) => contract.parentContract?.id === msaContract.id
    )
  })

  // An SA -- or a Non-Contract, which follows the same rule -- is deletable
  // while it has no services.
  //
  // Non-Contract used to be missed here: this filtered on
  // `contractType.label === 'SA'`, so a Non-Contract matched neither branch and
  // kept whatever canBeDeleted the server last sent, going stale as soon as its
  // services changed in-session. The two types are the same kind of thing
  // everywhere else (documentation/rules/contract scopes the msService
  // restriction to MSA alone), and hasSAOrNonContractContractType is the
  // predicate that says so.
  //
  // Both filters now key off the contractType ID rather than its display label.
  const saOrNonContractContracts = contracts.filter(
    hasSAOrNonContractContractType
  )
  saOrNonContractContracts.forEach((saOrNonContract) => {
    saOrNonContract.canBeDeleted =
      saOrNonContract.status.id === ContractStatusIds.NO_SERVICES
  })
}
